import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { GoogleMap, useJsApiLoader, MarkerF, InfoWindowF, MarkerClustererF, HeatmapLayerF } from "@react-google-maps/api";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { Navigation, Info, X, MapPin, Truck, User, RefreshCw, Layers, ThermometerSun } from "lucide-react";
import DTLifecycleModal from "./DTLifecycleModal";
import { toast } from "sonner";

// Status color mapping
const STATUS_COLORS = {
  pending: "#808080",
  ci_done: "#FFD700",
  dtm_done: "#FFA500",
  dtc_done: "#800080",
  mi_done: "#008000",
  qc1_pending: "#008000",
  qc_passed: "#008000",
  qc_rejected: "#FF0000",
  rework: "#FF0000",
  paid: "#008000"
};

const STATUS_LABELS = {
  pending: "Survey Pending",
  ci_done: "DTM Pending",
  dtm_done: "DTC Pending",
  dtc_done: "MI Pending",
  mi_done: "Awaiting QC",
  qc1_pending: "QC Pending",
  qc_passed: "QC Passed",
  qc_rejected: "QC Rejected",
  rework: "Rework Required",
  paid: "Paid"
};

// Default center (Varanasi, UP, India)
const DEFAULT_CENTER = { lat: 25.3176, lng: 82.9739 };

// Map container style
const containerStyle = {
  width: "100%",
  height: "100%"
};

// Libraries for heatmap
const libraries = ["visualization", "places"];

// Cache configuration
const CACHE_KEYS = {
  PINS: "map_pins_cache",
  VEHICLES: "map_vehicles_cache",
  HEATMAP: "map_heatmap_cache",
  TIMESTAMP: "map_cache_timestamp",
  USER: "map_cache_user"
};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// Cache helper functions
const getCache = (key) => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

const setCache = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    localStorage.setItem(CACHE_KEYS.TIMESTAMP, Date.now().toString());
  } catch (e) {
    console.warn("Cache write failed:", e);
  }
};

const isCacheValid = (userId) => {
  const timestamp = localStorage.getItem(CACHE_KEYS.TIMESTAMP);
  if (!timestamp) return false;
  const cachedUser = localStorage.getItem(CACHE_KEYS.USER);
  if (cachedUser !== userId) return false;
  return (Date.now() - parseInt(timestamp)) < CACHE_DURATION;
};

const clearMapCache = () => {
  Object.values(CACHE_KEYS).forEach(key => localStorage.removeItem(key));
};

export default function MapView({ mode = "field" }) {
  const { user } = useAuth();
  const mapRef = useRef(null);
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPin, setSelectedPin] = useState(null);
  const [showLifecycleModal, setShowLifecycleModal] = useState(false);
  const [selectedDtId, setSelectedDtId] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [heatmapData, setHeatmapData] = useState([]);
  const [showLegend, setShowLegend] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [zoom, setZoom] = useState(12);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [usingCache, setUsingCache] = useState(false);
  const [noAssignment, setNoAssignment] = useState(false);
  const [noAssignmentMessage, setNoAssignmentMessage] = useState("");

  // Load Google Maps API
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY || "",
    libraries
  });

  // Get user's current location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(loc);
          setCenter(loc);
        },
        (error) => {
          console.log("Geolocation error:", error);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, []);

  // Fetch map pins with caching
  const fetchPins = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    const userId = user?.id || "";
    
    // Check cache first (unless force refresh or filter changed)
    if (!forceRefresh && !statusFilter && isCacheValid(userId)) {
      const cachedPins = getCache(CACHE_KEYS.PINS);
      if (cachedPins) {
        setPins(cachedPins);
        setUsingCache(true);
        setLastUpdated(new Date(parseInt(localStorage.getItem(CACHE_KEYS.TIMESTAMP))));
        setLoading(false);
        
        // Center on first pin if no user location
        if (cachedPins.length > 0 && !userLocation) {
          const firstPin = cachedPins[0];
          if (firstPin.gps_lat && firstPin.gps_lng) {
            setCenter({ lat: firstPin.gps_lat, lng: firstPin.gps_lng });
          }
        }
        return;
      }
    }
    
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      
      const res = await api.get("/maps/pins", { params });
      const pinsData = res.data.pins || [];
      setPins(pinsData);
      setUsingCache(false);
      setLastUpdated(new Date());
      
      // Check if user has no assignment (for technicians/helpers)
      if (res.data.message && pinsData.length === 0) {
        setNoAssignment(true);
        setNoAssignmentMessage(res.data.message);
      } else {
        setNoAssignment(false);
        setNoAssignmentMessage("");
      }
      
      // Cache the data with user ID (only if no filter applied)
      if (!statusFilter) {
        setCache(CACHE_KEYS.PINS, pinsData);
        localStorage.setItem(CACHE_KEYS.USER, userId);
      }
      
      // Center on first pin if no user location
      if (pinsData.length > 0 && !userLocation) {
        const firstPin = pinsData[0];
        if (firstPin.gps_lat && firstPin.gps_lng) {
          setCenter({ lat: firstPin.gps_lat, lng: firstPin.gps_lng });
        }
      }
    } catch (e) {
      console.error("Failed to fetch pins:", e);
      toast.error("Failed to load map data");
      
      // Fall back to cache on error (only if same user)
      if (isCacheValid(userId)) {
        const cachedPins = getCache(CACHE_KEYS.PINS);
        if (cachedPins) {
          setPins(cachedPins);
          setUsingCache(true);
          toast.info("Showing cached data");
        }
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, userLocation, user]);

  // Fetch vehicles with caching
  const fetchVehicles = useCallback(async (forceRefresh = false) => {
    if (!["supervisor", "technician", "helper", "admin"].includes(user?.role)) return;
    
    // Check cache first
    if (!forceRefresh && isCacheValid()) {
      const cachedVehicles = getCache(CACHE_KEYS.VEHICLES);
      if (cachedVehicles) {
        setVehicles(cachedVehicles);
        return;
      }
    }
    
    try {
      const res = await api.get("/maps/vehicles/live");
      const vehiclesData = res.data.vehicles || [];
      setVehicles(vehiclesData);
      setCache(CACHE_KEYS.VEHICLES, vehiclesData);
    } catch (e) {
      console.log("Vehicle fetch error:", e);
      // Fall back to cache
      const cachedVehicles = getCache(CACHE_KEYS.VEHICLES);
      if (cachedVehicles) setVehicles(cachedVehicles);
    }
  }, [user]);

  // Fetch technician positions (for supervisor)
  const fetchTechnicians = useCallback(async () => {
    if (user?.role !== "supervisor") return;
    
    try {
      const res = await api.get("/maps/technicians/live");
      setTechnicians(res.data.technicians || []);
    } catch (e) {
      console.log("Technician fetch error:", e);
    }
  }, [user]);

  // Fetch heatmap data (for admin)
  const fetchHeatmap = useCallback(async () => {
    if (!["admin", "package_head", "ci"].includes(user?.role)) return;
    
    try {
      const res = await api.get("/maps/heatmap");
      setHeatmapData(res.data.heatmap_points || []);
    } catch (e) {
      console.log("Heatmap fetch error:", e);
    }
  }, [user]);

  useEffect(() => {
    fetchPins();
    fetchVehicles();
    fetchTechnicians();
    
    if (showHeatmap) {
      fetchHeatmap();
    }
    
    // Refresh vehicle positions every 30 seconds
    const vehicleInterval = setInterval(fetchVehicles, 30000);
    const techInterval = setInterval(fetchTechnicians, 30000);
    
    return () => {
      clearInterval(vehicleInterval);
      clearInterval(techInterval);
    };
  }, [fetchPins, fetchVehicles, fetchTechnicians, fetchHeatmap, showHeatmap]);

  // Handle native navigation
  const handleNavigate = (lat, lng) => {
    // Use native OS navigation deep link (free)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const url = isIOS
      ? `maps://?daddr=${lat},${lng}`
      : `google.navigation:q=${lat},${lng}`;
    
    // Fallback to Google Maps web
    const webUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    
    try {
      window.open(url, "_blank");
    } catch {
      window.open(webUrl, "_blank");
    }
  };

  // Handle info button click (open lifecycle modal)
  const handleInfoClick = (dtId) => {
    setSelectedDtId(dtId);
    setShowLifecycleModal(true);
    setSelectedPin(null);
  };

  // Geo-fence validation for "Start Work"
  const validateGeoFence = async (dtId, dtLat, dtLng) => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation not supported"));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const res = await api.post("/maps/geofence/validate", null, {
              params: {
                dt_id: dtId,
                device_lat: position.coords.latitude,
                device_lng: position.coords.longitude
              }
            });
            
            if (res.data.valid) {
              resolve(res.data);
            } else {
              reject(new Error(res.data.message));
            }
          } catch (e) {
            reject(new Error(e.response?.data?.detail || "Geo-fence validation failed"));
          }
        },
        (error) => {
          reject(new Error("Unable to get your location. Please enable GPS."));
        },
        { enableHighAccuracy: true, timeout: 15000 }
      );
    });
  };

  // Create marker icon with status color
  const createMarkerIcon = (color, isRework = false) => {
    return {
      path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
      fillColor: color,
      fillOpacity: 1,
      strokeColor: isRework ? "#FF0000" : "#FFFFFF",
      strokeWeight: isRework ? 3 : 2,
      scale: 1.5,
      anchor: { x: 12, y: 24 }
    };
  };

  // Memoize heatmap data for Google Maps
  const heatmapPoints = useMemo(() => {
    if (!isLoaded || !window.google) return [];
    return heatmapData.map(point => ({
      location: new window.google.maps.LatLng(point.lat, point.lng),
      weight: point.weight
    }));
  }, [heatmapData, isLoaded]);

  // Cluster options
  const clusterOptions = {
    imagePath: "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m",
    gridSize: 60,
    minimumClusterSize: 5,
    maxZoom: 15
  };

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-full bg-slate-100">
        <div className="text-center p-8">
          <MapPin size={48} className="mx-auto text-red-500 mb-4" />
          <h3 className="text-lg font-bold text-slate-800">Map Loading Error</h3>
          <p className="text-slate-500">Please check your Google Maps API key</p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-full bg-slate-100">
        <RefreshCw size={32} className="animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="relative h-full" data-testid="map-view">
      {/* Map Controls */}
      <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-white border border-slate-200 rounded-sm px-3 py-2 text-sm font-medium shadow-md"
          data-testid="status-filter"
        >
          <option value="">All Statuses</option>
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>

        {/* Layer toggles for admin */}
        {["admin", "package_head", "ci"].includes(user?.role) && (
          <div className="bg-white border border-slate-200 rounded-sm p-2 shadow-md">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={showHeatmap}
                onChange={(e) => setShowHeatmap(e.target.checked)}
                className="rounded"
              />
              <ThermometerSun size={16} className="text-orange-500" />
              Heatmap
            </label>
          </div>
        )}
      </div>

      {/* Refresh Button */}
      <button
        onClick={() => { fetchPins(); fetchVehicles(); fetchTechnicians(); }}
        className="absolute top-4 right-4 z-10 bg-white border border-slate-200 rounded-sm p-2 shadow-md hover:bg-slate-50"
        data-testid="refresh-map"
      >
        <RefreshCw size={18} className={loading ? "animate-spin text-orange-500" : "text-slate-600"} />
      </button>

      {/* Legend */}
      {showLegend && (
        <div className="absolute bottom-4 left-4 z-10 bg-white border border-slate-200 rounded-sm p-3 shadow-md max-w-[200px]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-slate-600">LEGEND</span>
            <button onClick={() => setShowLegend(false)} className="text-slate-400 hover:text-slate-600">
              <X size={14} />
            </button>
          </div>
          <div className="space-y-1.5">
            {Object.entries(STATUS_COLORS).filter(([k]) => !["qc1_pending", "paid", "rework"].includes(k)).map(([status, color]) => (
              <div key={status} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                <span className="text-xs text-slate-600">{STATUS_LABELS[status]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats Badge */}
      <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 bg-white border border-slate-200 rounded-sm px-4 py-2 shadow-md">
        <span className="text-sm font-bold text-slate-800">{pins.length} DTs</span>
        {vehicles.length > 0 && (
          <span className="text-sm text-slate-500 ml-2">• {vehicles.length} Vehicles</span>
        )}
      </div>

      {/* No Assignment Banner for Technicians/Helpers */}
      {noAssignment && (
        <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-20 bg-yellow-50 border-2 border-yellow-400 rounded-lg px-6 py-4 shadow-lg max-w-md" data-testid="no-assignment-banner">
          <div className="flex items-start gap-3">
            <MapPin size={24} className="text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-yellow-800">No Feeder Assignment</div>
              <div className="text-sm text-yellow-700 mt-1">{noAssignmentMessage || "You have no feeder assignment for today."}</div>
              <div className="text-xs text-yellow-600 mt-2 bg-yellow-100 p-2 rounded">
                <strong>💡 Tip:</strong> Contact your Supervisor to be added to today's Daily Roster. Once assigned, you'll see DT pins for your feeder.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Google Map */}
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={zoom}
        onLoad={(map) => { mapRef.current = map; }}
        onZoomChanged={() => {
          if (mapRef.current) {
            setZoom(mapRef.current.getZoom());
          }
        }}
        options={{
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          styles: [
            { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }
          ]
        }}
      >
        {/* User Location Marker */}
        {userLocation && (
          <MarkerF
            position={userLocation}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              fillColor: "#4285F4",
              fillOpacity: 1,
              strokeColor: "#FFFFFF",
              strokeWeight: 3,
              scale: 10
            }}
            title="Your Location"
          />
        )}

        {/* DT Pins with Clustering */}
        <MarkerClustererF options={clusterOptions}>
          {(clusterer) =>
            pins.map((pin) => (
              <MarkerF
                key={pin.id}
                position={{ lat: pin.gps_lat, lng: pin.gps_lng }}
                clusterer={clusterer}
                icon={createMarkerIcon(pin.color || STATUS_COLORS[pin.lifecycle_status], pin.lifecycle_status === "qc_rejected")}
                onClick={() => setSelectedPin(pin)}
                title={`${pin.dt_code} - ${STATUS_LABELS[pin.lifecycle_status]}`}
              />
            ))
          }
        </MarkerClustererF>

        {/* Vehicle Markers (Truck Icon) */}
        {vehicles.map((vehicle) => vehicle.live_location?.lat && (
          <MarkerF
            key={`vehicle-${vehicle.id}`}
            position={{ lat: vehicle.live_location.lat, lng: vehicle.live_location.lng }}
            icon={{
              url: "data:image/svg+xml," + encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32" fill="#3B82F6">
                  <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                </svg>
              `),
              scaledSize: new window.google.maps.Size(32, 32),
              anchor: new window.google.maps.Point(16, 16)
            }}
            title={`Vehicle: ${vehicle.vehicle_number}`}
          />
        ))}

        {/* Technician Markers (for Supervisor view) */}
        {technicians.map((tech) => (
          <MarkerF
            key={`tech-${tech.technician_id}`}
            position={{ lat: tech.lat, lng: tech.lng }}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              fillColor: "#10B981",
              fillOpacity: 1,
              strokeColor: "#FFFFFF",
              strokeWeight: 2,
              scale: 8
            }}
            title={tech.technician_name}
          />
        ))}

        {/* Heatmap Layer */}
        {showHeatmap && heatmapPoints.length > 0 && (
          <HeatmapLayerF
            data={heatmapPoints}
            options={{
              radius: 50,
              opacity: 0.6,
              gradient: [
                "rgba(0, 255, 0, 0)",
                "rgba(0, 255, 0, 1)",
                "rgba(255, 255, 0, 1)",
                "rgba(255, 128, 0, 1)",
                "rgba(255, 0, 0, 1)"
              ]
            }}
          />
        )}

        {/* Info Window (Stage 1 - Snippet) */}
        {selectedPin && (
          <InfoWindowF
            position={{ lat: selectedPin.gps_lat, lng: selectedPin.gps_lng }}
            onCloseClick={() => setSelectedPin(null)}
          >
            <div className="min-w-[250px] max-w-[300px] p-1" data-testid="pin-info-window">
              {/* Header */}
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">{selectedPin.snippet?.header}</h3>
                  <span 
                    className="inline-block px-2 py-0.5 rounded text-[10px] font-bold text-white mt-1"
                    style={{ backgroundColor: selectedPin.color }}
                  >
                    {STATUS_LABELS[selectedPin.lifecycle_status]}
                  </span>
                </div>
              </div>

              {/* Dynamic Body Content */}
              <div className="bg-slate-50 rounded p-2 mb-3 text-xs space-y-1">
                {selectedPin.snippet?.body && Object.entries(selectedPin.snippet.body).map(([key, value]) => {
                  if (!value || key === "message") return null;
                  
                  // Special styling for reject reason
                  if (key === "qc_reject_reason") {
                    return (
                      <div key={key} className="text-red-600 font-bold">
                        ⚠️ {value}
                      </div>
                    );
                  }
                  
                  // Shutdown status
                  if (key === "shutdown_status") {
                    return (
                      <div key={key} className={`font-bold ${value === "ACTIVE" ? "text-green-600" : "text-red-500"}`}>
                        Shutdown: {value}
                      </div>
                    );
                  }
                  
                  // Format key
                  const label = key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                  return (
                    <div key={key} className="flex justify-between">
                      <span className="text-slate-500">{label}:</span>
                      <span className="font-medium text-slate-700">{value}</span>
                    </div>
                  );
                })}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                {/* Navigate Button */}
                <button
                  onClick={() => handleNavigate(selectedPin.gps_lat, selectedPin.gps_lng)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-500 text-white rounded text-xs font-bold hover:bg-blue-600"
                  data-testid="navigate-btn"
                >
                  <Navigation size={14} />
                  Navigate
                </button>

                {/* Info Button (Opens Lifecycle Modal) */}
                <button
                  onClick={() => handleInfoClick(selectedPin.id)}
                  className="flex items-center justify-center px-3 py-2 bg-slate-200 text-slate-700 rounded text-xs font-bold hover:bg-slate-300"
                  data-testid="info-btn"
                >
                  <Info size={14} />
                </button>
              </div>
            </div>
          </InfoWindowF>
        )}
      </GoogleMap>

      {/* Lifecycle Modal (Stage 2) */}
      {showLifecycleModal && selectedDtId && (
        <DTLifecycleModal
          dtId={selectedDtId}
          onClose={() => { setShowLifecycleModal(false); setSelectedDtId(null); }}
          onStartWork={validateGeoFence}
        />
      )}

      {/* Show Legend Toggle */}
      {!showLegend && (
        <button
          onClick={() => setShowLegend(true)}
          className="absolute bottom-4 left-4 z-10 bg-white border border-slate-200 rounded-sm p-2 shadow-md"
        >
          <Layers size={18} className="text-slate-600" />
        </button>
      )}
    </div>
  );
}
